// App propia de Farmacia Interna (2026-09-11) — pantalla de inicio del
// Director Técnico Q.F./B.F.: NO reusa el <TopNav> genérico (Dashboard/
// Pacientes/Agenda/Historias/Reportes) a propósito, porque este rol no debe
// ver ninguna de esas secciones (pedido explícito: "solo la pestaña de
// farmacia interna y de medicamentos"). Layout self-contained con su propio
// subnav de 4 vistas.
import type { JSX } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { cerrarSesionHce } from '@/auth/cerrarSesion';

// "Vender" (venta de mostrador de un solo paso) se retiró (2026-09-11):
// Órdenes ya cubre el mismo caso con sus 4 pasos, sin duplicar el punto de
// venta. Toda venta pasa por Órdenes de ahora en adelante.
const TABS = ['cola', 'catalogo', 'inventario', 'historial', 'facturacion'] as const;
const RUTA_DE: Record<(typeof TABS)[number], string> = {
  cola: '/farmacia',
  catalogo: '/farmacia/catalogo',
  inventario: '/farmacia/inventario',
  historial: '/farmacia/historial',
  facturacion: '/farmacia/facturacion',
};

// Sub-nav de Facturación (Fase 4, 2026-09-11) — Órdenes/Comprobantes/
// Convenios/Caja/Reportes son sub-secciones del mismo trabajo de oficina, no
// cinco tabs sueltos en la barra principal (que ya tiene 5 con las de
// mostrador). Mismo criterio que admin/facturacion-subnav.tsx del CRM.
const SUBTABS = ['ordenes', 'comprobantes', 'convenios', 'caja', 'reportes'] as const;
const SUBRUTA_DE: Record<(typeof SUBTABS)[number], string> = {
  ordenes: '/farmacia/facturacion',
  comprobantes: '/farmacia/facturacion/comprobantes',
  convenios: '/farmacia/facturacion/convenios',
  caja: '/farmacia/facturacion/caja',
  reportes: '/farmacia/facturacion/reportes',
};

export function FarmaciaApp(): JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const enFacturacion = pathname.startsWith('/farmacia/facturacion');

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <span className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.marca')}</span>
        <nav className="flex flex-wrap gap-1 text-xs font-mono uppercase tracking-wide">
          {TABS.map((tab) => (
            <NavLink
              key={tab}
              to={RUTA_DE[tab]}
              end={tab === 'cola'}
              className={({ isActive }) =>
                isActive || (tab === 'facturacion' && enFacturacion)
                  ? 'rounded-md bg-hce-pine/10 px-2.5 py-1.5 text-hce-pine'
                  : 'rounded-md px-2.5 py-1.5 text-hce-muted hover:text-hce-ink'
              }
            >
              {t(`farmaciaApp.tabs.${tab}`)}
            </NavLink>
          ))}
        </nav>
        <button
          type="button"
          onClick={() => navigate('/mi-certificado')}
          className="text-xs text-hce-muted underline-offset-2 hover:text-hce-ink hover:underline"
        >
          {t('farmaciaApp.miCertificado')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/seguridad')}
          className="text-xs text-hce-muted underline-offset-2 hover:text-hce-ink hover:underline"
        >
          {t('farmaciaApp.seguridad')}
        </button>
        <Button variant="outline" size="sm" onClick={() => void cerrarSesionHce(medplum, navigate)}>
          {t('farmaciaApp.cerrarSesion')}
        </Button>
      </div>
      {enFacturacion && (
        <nav className="mb-4 flex flex-wrap gap-1 text-xs font-mono uppercase tracking-wide">
          {SUBTABS.map((tab) => (
            <NavLink
              key={tab}
              to={SUBRUTA_DE[tab]}
              end={tab === 'ordenes'}
              className={({ isActive }) =>
                isActive
                  ? 'rounded-md border border-hce-pine/30 bg-hce-pine/10 px-2.5 py-1 text-hce-pine'
                  : 'rounded-md border border-transparent px-2.5 py-1 text-hce-muted hover:text-hce-ink'
              }
            >
              {t(`farmaciaApp.facturacion.tabs.${tab}`)}
            </NavLink>
          ))}
        </nav>
      )}
      <Outlet />
    </div>
  );
}
